<template>
    <div>
        <div class="level">
            <div class="level-left">
                <h2 class="title is-size-5">{{ headline }}</h2>
            </div>
            <div class="level-right">
                <button class="button is-default mr-2" @click.prevent="reset" :disabled="!filtersActive">Nulstil filtre</button>
                <button class="button is-primary" @click.prevent="openModal(null)">Opret</button>
            </div>
        </div>
        <div class="columns is-tablet">
            <form-field
                class="column pb-0"
                inner-container-class="select is-fullwidth"
            >
                <input-select
                    :options="priceTypeOptions"
                    v-model="priceTypeId"
                    optionValueProp="id"
                    optionTitleProp="name"
                    @input="priceTypeChanged"
                />
            </form-field>
        </div>
        <div class="columns is-tablet">
            <form-field
                class="column pb-0"
                inner-container-class="select is-fullwidth"
            >
                <input-select
                    :options="deliveryTypeOptions"
                    v-model="deliveryTypeId"
                    optionValueProp="id"
                    optionTitleProp="name"
                    @input="deliveryTypeChanged"
                />
            </form-field>
        </div>
        <div class="columns">
            <div class="column is-full">
                <List
                    v-if="listManufacturers.length > 0"
                    table-classes="table is-fullwidth"
                    :columns="columns"
                    :data="listManufacturers"
                    :page-length="50"
                    :rows-clickable="false"
                    :default-order="{ col: 'name', dir: 'asc' }"
                    :search="search"
                    @search="onSearch"
                >
                    <template slot="column-actions" slot-scope="{ row }">
                        <a class="button is-small" @click.stop="openModal(row)" title="Rediger">
                            <span class="icon">
                                <i class="fas fa-edit" />
                            </span>
                        </a>
                        <a class="button is-small" @click.stop="openModal(row, 'priceTypes')" title="Rediger pristyper">
                            <span class="icon">
                                <i class="fas fa-list" />
                            </span>
                        </a>
                    </template>
                </List>
                <span v-else class="list-empty">Ingen fabrikanter fundet</span>
            </div>
        </div>

        <ManufacturerModal
            :active="modalOpen"
            :manufacturer="modalManufacturer"
            @closed="closeModal"
            @updated="updated"
            @created="created"
            @deleted="deleted"
            ref="modal"
        />
    </div>
</template>

<script>
import {InputText, FormField, InputSelect} from '@elems/form'
import List from "@/elements/list";
import ManufacturerModal from "./components/ManufacturerModal";
import {mapGetters} from "vuex";

export default {
    components: { ManufacturerModal, List, FormField, InputText, InputSelect },
    props: {
        headline: String,
        tab: String,
    },
    computed: {
        ...mapGetters([
            'listDeliveryTypes',
            'listPriceTypes'
        ]),
        deliveryTypeOptions() {
            return [
                { id: null, name: 'Alle leveringstider' },
                ...this.listDeliveryTypes,
            ]
        },
        priceTypeOptions() {
            return [
                { id: null, name: 'Alle pristyper' },
                ...this.listPriceTypes,
            ]
        },
        filtersActive() {
            return this.search || this.deliveryTypeId || this.priceTypeId
        }
    },
    data() {
        return {
            listManufacturers: [],
            modalOpen: false,
            modalManufacturer: null,
            deliveryTypeId: null,
            priceTypeId: null,
            search: '',
            columns: [
                { 
                    name: 'logo', 
                    title: 'Logo', 
                    sortable: false, 
                    searchable: false, 
                    width: 10, 
                    template: (row) => {
                        if (row.logo) {
                            return `<div class="manufacturer-list-image"><img src="${row.logo}" alt="${row.name}" /></div>`
                        }
                        return '<div class="has-text-centered">—</div>'
                    }
                },
                { name: 'name', title: 'Fabrikant', sortable: true, searchable: true, width: 70, hasTooltip: true, },
                { name: 'created_at', hidden: true },
                { name: 'actions', title: '', sortable: false, searchable: false, classes:() => 'has-text-right', width: 20 },
            ],
        }
    },
    methods: {
        onSearch(value) {
            this.search = value
            this.updateUrlParams()
        },
        reset() {
            this.search = ''
            this.deliveryTypeId = null
            this.priceTypeId = null
            this.updateUrlParams()
            this.updateList()
        },
        updateUrlParams() {
            let params = {
                tab: this.tab,
            };

            if (this.search) {
                params.search = this.search
            }

            if (this.deliveryTypeId) {
                params['delivery-type'] = this.deliveryTypeId;
            }

            if (this.priceTypeId) {
                params['price-type'] = this.priceTypeId;
            }

            this.$router.push({ query: params })
        },
        deliveryTypeChanged(deliveryTypeId) {
            this.updateUrlParams()
            this.updateList()
        },
        priceTypeChanged(priceTypeId) {
            this.updateUrlParams()
            this.updateList()
        },
        openModal(manufacturer = null, tab = 'details') {
            this.modalOpen = true
            this.modalManufacturer = manufacturer
            this.$refs.modal.activeTab = tab
        },
        closeModal() {
            this.modalOpen = false
            this.modalManufacturer = null
        },
        updateList() {
            const params = {
                with: 'localizations,priceTypes.priceType.localizations.locale,priceTypes.deliveryType.localizations.locale,priceTypes.priceType.condition',
            };

            if (this.deliveryTypeId) {
                params['delivery-type'] = this.deliveryTypeId;
            }

            if (this.priceTypeId) {
                params['price-type'] = this.priceTypeId;
            }

            window.axios.get('/api/manufacturers', { params })
                .then(({ data }) => {
                    this.listManufacturers = data
                })
        },
        updated(manufacturer) {
            this.updateList()
        },
        created(manufacturer) {
            this.listManufacturers.push(manufacturer)
            this.modalManufacturer = manufacturer
        },
        deleted(manufacturer) {
            this.listManufacturers = this.listManufacturers.filter((item) => item.id !== manufacturer.id)
            
            this.closeModal();
        },
    },
    mounted() {
        let params = new URLSearchParams(window.location.search)
        this.deliveryTypeId = params.get('delivery-type')
        this.priceTypeId = params.get('price-type')
        this.search = params.get('search') || ''

        this.updateList()
        this.$store.dispatch('loadDeliveryTypes')
        this.$store.dispatch('loadPriceTypes')
    },
}
</script>

<style lang="scss">
.manufacturer-list-image {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 30px;

    & > img {
        max-height: 30px;
    }
}
</style>